import { useState, useMemo } from 'react';
import { BusinessReview } from '../../../../mocks/directoryDetails';
import WriteReviewForm from './WriteReviewForm';

interface ReviewsSectionProps {
  rating: number;
  totalReviews: number;
  reviewsList: BusinessReview[];
  businessName: string;
}

export default function ReviewsSection({
  rating,
  totalReviews,
  reviewsList,
  businessName,
}: ReviewsSectionProps) {
  const [visibleCount, setVisibleCount] = useState(3);
  const [sortOption, setSortOption] = useState<'newest' | 'highest' | 'lowest'>('newest');
  const [sortOpen, setSortOpen] = useState(false);

  const sortedReviews = useMemo(() => {
    const copy = [...reviewsList];
    switch (sortOption) {
      case 'newest':
        return copy.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      case 'highest':
        return copy.sort((a, b) => b.rating - a.rating);
      case 'lowest':
        return copy.sort((a, b) => a.rating - b.rating);
      default:
        return copy;
    }
  }, [reviewsList, sortOption]);

  const sortOptions = [
    { value: 'newest', label: 'Most Recent' },
    { value: 'highest', label: 'Highest Rating' },
    { value: 'lowest', label: 'Lowest Rating' }
  ];

  // Generate rating distribution
  const distribution = [5, 4, 3, 2, 1].map((star) => {
    const count = reviewsList.filter((r) => Math.round(r.rating) === star).length;
    const pct =
      totalReviews > 0 ? Math.round((count / reviewsList.length) * 100) : 0;
    return { star, count, pct };
  });

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
      <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-chat-quote-line text-teal-600"></i>
        </div>
        Reviews
      </h3>

      {/* Summary */}
      <div className="flex items-start gap-8 mb-6 pb-6 border-b border-gray-100">
        <div className="text-center flex-shrink-0">
          <div className="text-4xl font-black text-gray-900">{rating}</div>
          <div className="flex items-center gap-0.5 mt-1 justify-center">
            {Array.from({ length: 5 }).map((_, i) => (
              <i
                key={i}
                className={`text-sm ${
                  i < Math.floor(rating)
                    ? 'ri-star-fill text-amber-400'
                    : i < rating
                    ? 'ri-star-half-fill text-amber-400'
                    : 'ri-star-line text-gray-300'
                }`}
              ></i>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1">{totalReviews} reviews</p>
        </div>

        <div className="flex-1 space-y-1.5">
          {distribution.map((d) => (
            <div key={d.star} className="flex items-center gap-2">
              <span className="text-xs text-gray-600 w-3 text-right">
                {d.star}
              </span>
              <i className="ri-star-fill text-amber-400 text-xs"></i>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-400 rounded-full transition-all"
                  style={{ width: `${d.pct}%` }}
                ></div>
              </div>
              <span className="text-xs text-gray-400 w-8">{d.pct}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Write a Review */}
      <div className="mb-6 pb-6 border-b border-gray-100">
        <WriteReviewForm businessName={businessName} />
      </div>

      {/* Sort Controls */}
      {reviewsList.length > 1 && (
        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-gray-500">
            Showing {Math.min(visibleCount, sortedReviews.length)} of {sortedReviews.length} reviews
          </p>
          <div className="relative">
            <button
              onClick={() => setSortOpen(!sortOpen)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-sort-desc text-gray-500 text-sm"></i>
              </div>
              {sortOptions.find((o) => o.value === sortOption)?.label}
              <i className={`ri-arrow-down-s-line text-gray-400 text-sm transition-transform ${sortOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {sortOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setSortOpen(false)}></div>
                <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-1 overflow-hidden">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setSortOption(opt.value as any);
                        setSortOpen(false);
                        setVisibleCount(3);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer flex items-center gap-2 ${
                        sortOption === opt.value
                          ? 'text-teal-700 bg-teal-50 font-semibold'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-sm ${
                          opt.value === 'newest' ? 'ri-time-line' : opt.value === 'highest' ? 'ri-arrow-up-line' : 'ri-arrow-down-line'
                        } ${sortOption === opt.value ? 'text-teal-600' : 'text-gray-400'}`}></i>
                      </div>
                      {opt.label}
                      {sortOption === opt.value && (
                        <i className="ri-check-line text-teal-600 text-sm ml-auto"></i>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Individual reviews */}
      <div className="space-y-5">
        {sortedReviews.slice(0, visibleCount).map((review) => (
          <article
            key={review.id}
            className="pb-5 border-b border-gray-50 last:border-0 last:pb-0"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-teal-700">
                  {review.avatar}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-gray-900">
                    {review.author}
                  </span>
                  {review.verified && (
                    <span className="text-[10px] font-bold text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                      Verified
                    </span>
                  )}
                  <span className="text-xs text-gray-400 ml-auto whitespace-nowrap">
                    {review.date}
                  </span>
                </div>
                <div className="flex items-center gap-0.5 mb-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`text-xs ${
                        i < review.rating
                          ? 'ri-star-fill text-amber-400'
                          : 'ri-star-line text-gray-300'
                      }`}
                    ></i>
                  ))}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {review.text}
                </p>

                <div className="flex items-center gap-3 mt-2">
                  <button className="flex items-center gap-1 text-xs text-gray-400 hover:text-teal-600 transition-colors cursor-pointer">
                    <i className="ri-thumb-up-line"></i>
                    Helpful ({review.helpful})
                  </button>
                </div>

                {review.ownerReply && (
                  <div className="mt-3 ml-4 pl-4 border-l-2 border-teal-200 bg-teal-50/50 rounded-r-lg p-3">
                    <p className="text-xs font-semibold text-teal-700 mb-1">
                      <i className="ri-reply-line mr-1"></i> Owner Response
                    </p>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      {review.ownerReply}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      {visibleCount < sortedReviews.length && (
        <button
          onClick={() => setVisibleCount(sortedReviews.length)}
          className="w-full mt-5 py-2.5 text-sm font-semibold text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          Show All {sortedReviews.length} Reviews
        </button>
      )}

      {/* Review Source Citation */}
      <div className="mt-6 pt-4 border-t border-gray-100">
        <div className="flex items-start gap-2">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-gray-400 text-sm"></i>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1">About Our Reviews</p>
            <p className="text-[10px] text-gray-400 leading-relaxed">
              All reviews are submitted by verified customers through our platform. Reviews undergo moderation to ensure authenticity and compliance with community guidelines. Ratings are calculated from all customer feedback and updated in real-time. Verified badges indicate customers who have confirmed their visit or purchase.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
